import { FC, MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { Box, Chip, IconButton, InputAdornment, Link, Portal, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import Song from "../models/song";
import useGetCollection from "../hooks/getCollection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataGridPremium, GridColDef, GridListColDef, GridRenderCellParams, GridRowParams, GridRowsProp, GridToolbarQuickFilter, useGridApiRef } from "@mui/x-data-grid-premium";
import { getPublicSongImageUrl } from "../utils/fileUtils";
import { dataGridStyles } from "../styles/theme";
import useModal from "../hooks/useModal";
import SongDialog from "../components/songDialog";
import { formatDuration } from "../utils/formatUtils";
import useSongPlayer from "../hooks/useSongPlayer";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import useQuickFilterPortal from "../hooks/useQuickFilterPortal";
import Genre from "../models/genre";
import Mood from "../models/mood";
import ScreenBase from "./ScreenBase";
import useQueryStringParams from "../hooks/useQueryStringParams";
import useMediaQueryStops from "../hooks/useMediaQueryStops";
import posthog from "posthog-js";

const TunesScreen: FC = () => {
	const { data: songData, loading } = useGetCollection<Song[]>('/song/all');

	const [columns, setColumns] = useState<GridColDef[]>([]);
	const [rows, setRows] = useState<GridRowsProp>([]);

	const { modals, show: showModal, hide: hideModal } = useModal();
	const portal = useQuickFilterPortal();
	const songPlayer = useSongPlayer();
	const theme = useTheme();
	const stops = useMediaQueryStops();
	const { getQueryParam, setQueryParam } = useQueryStringParams();

	const gridApiRef = useGridApiRef();
	const quickFilterRef = useRef<HTMLInputElement | null>(null);
	const initialFilterSet = useRef(false);

	const openSongDialog = useCallback(async (song: Song) => {
		setQueryParam('songId', song.id);

		await showModal(
			900,
			{ prefix: 'fas', iconName: 'music' },
			song.title,
			<SongDialog song={song} />,
			false
		);

		setQueryParam('songId');
	}, [setQueryParam, showModal]);

	const CustomToolbar = useCallback(() => {
		const handleFilterClear = () => {
			portal.setFilterText('');
			gridApiRef.current?.setQuickFilterValues([]);
			setQueryParam('q');

			// Delay this one frame because the setQueryParam call will cause a re-render
			setTimeout(() => {
				quickFilterRef.current?.focus();
			});
		};

		return (
			<>
				<Portal container={() => document.getElementById('search-container') || null}>
					<GridToolbarQuickFilter
						inputRef={(node) => {
							quickFilterRef.current = node;

							if (node && portal.filterText && !initialFilterSet.current) {
								node.value = portal.filterText;
								initialFilterSet.current = true;
							}
						}}
						variant="outlined"
						size="small"
						placeholder="Search by title, album, genre, mood, or BPM"
						sx={{ width: '100%', height: 36, padding: 0 }}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} />
									</InputAdornment>
								),
								endAdornment: quickFilterRef.current?.value && (
									<InputAdornment position="end">
										<IconButton
											size="small"
											sx={{ width: 28, height: 28 }}
											onClick={handleFilterClear}
										>
											<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'times' }} style={{ color: theme.palette.text.secondary }} />
										</IconButton>
									</InputAdornment>
								)
							}
						}}
					/>
				</Portal>
			</>
		);
	}, [gridApiRef, portal, setQueryParam, theme.palette.text.secondary]);

	useEffect(() => {
		const filterText = getQueryParam('q');

		if (filterText) {
			portal.setFilterText(filterText);
		}
	}, [getQueryParam, portal]);

	useEffect(() => {
		portal.setIsDataGridScreen(true);

		return () => {
			portal.setIsDataGridScreen(false);
		};
	}, [portal]);

	useEffect(() => {
		if (songData && songData.length) {
			const sortableSongData = [...songData];

			const songDataSorted = sortableSongData.sort((a, b) => {
				// Sort by album.releaseDate (descending) first, then by title
				const aDate = a.album?.releaseDate ? new Date(a.album.releaseDate).getTime() : 0;
				const bDate = b.album?.releaseDate ? new Date(b.album.releaseDate).getTime() : 0;

				// Sort by album release date descending
				if (bDate !== aDate) {
					return bDate - aDate;
				}

				// If release dates are the same, sort by title ascending
				return a.title.localeCompare(b.title);
			});

			setRows(songDataSorted);
		}

		const qsSongId = getQueryParam('songId');

		if (qsSongId) {
			const song = songData?.find(s => s.id === qsSongId);
			if (song) {
				if (modals.length === 0) {
					openSongDialog(song);
				} else if (modals[0].title !== song.title) {
					hideModal();

					setTimeout(() => {
						openSongDialog(song);
					}, 300);
				}
			}
		}
	}, [getQueryParam, hideModal, modals, openSongDialog, songData]);

	useEffect(() => {
		if (gridApiRef.current) {
			const values = portal.filterText ? portal.filterText.split(' ') : [];
			gridApiRef.current.setQuickFilterValues(values);
		}
	}, [gridApiRef, portal.filterText, rows]);

	const getPlayStateIcon = useCallback((song: Song): IconName => {
		if (songPlayer.song && songPlayer.song.id === song.id) {
			return songPlayer.isPlaying ? 'pause' : 'play';
		} else {
			return 'play';
		}
	}, [songPlayer.isPlaying, songPlayer.song]);

	const handlePlayPause = useCallback((song: Song) => (event: MouseEvent) => {
		event.stopPropagation();

		if (songPlayer.song && songPlayer.song.id === song.id) {
			if (songPlayer.isPlaying) {
				songPlayer.pause();
			} else {
				songPlayer.resume();
			}
		} else {
			songPlayer.play(song);

			posthog.capture('song_played', {
				songId: song.id,
				songTitle: song.title,
				songAlbum: song.album?.name,
				origin: 'tunes_list'
			});
		}
	}, [songPlayer]);

	const genreClicked = useCallback((genre: Genre) => (event: MouseEvent) => {
		event.stopPropagation();

		portal.setFilterText(genre.genre);
		setQueryParam('q', genre.genre);
	}, [portal, setQueryParam]);

	const moodClicked = useCallback((mood: Mood) => (event: MouseEvent) => {
		event.stopPropagation();

		portal.setFilterText(mood.mood);
		setQueryParam('q', mood.mood);
	}, [portal, setQueryParam]);

	useEffect(() => {
		const handleDownloadClicked = (song: Song) => async (event: MouseEvent) => {
			event.stopPropagation();
			openSongDialog(song);
			
			posthog.capture('song_opened', {
				songId: song.id,
				songTitle: song.title,
				songAlbum: song.album?.name,
				origin: 'download_click'
			});
		};

		setColumns([
			{
				field: 'id',
				headerName: '',
				width: 68,
				disableColumnMenu: true,
				sortable: false,
				resizable: false,
				renderCell: (params) => (
					<Stack direction="row" justifyContent="flex-end" width={68}>
						<Tooltip title={(params.row as Song).album?.name || ''} placement="left">
							<Box
								component="img"
								src={getPublicSongImageUrl(params.row as Song)}
								alt={(params.row as Song).title}
								width={60}
								height={60}
								borderRadius={0.5}
							/>
						</Tooltip>
					</Stack>
				)
			},
			{
				field: 'title',
				headerName: 'Title',
				flex: 1,
				disableColumnMenu: true,
				resizable: false,
				renderCell: (params) => (
					<Stack spacing={0.5} minWidth={'0'}>
						<Stack direction="row" alignItems="baseline">
							<Typography variant="body1" noWrap color="textPrimary">{(params.row as Song).title}</Typography>
							<Typography variant="caption" noWrap color="textSecondary" sx={{ marginLeft: 1 }}>{(params.row as Song).album?.name}</Typography>
						</Stack>
						<Stack direction="row" spacing={1} overflow="hidden">
							{(params.row as Song).genres.map(g => (
								<Chip key={g.id} variant="outlined" size="small" color="secondary" label={g.genre} sx={{ textTransform: 'capitalize' }} onClick={genreClicked(g)} />
							))}
							{(params.row as Song).moods.map(m => (
								<Chip key={m.id} variant="outlined" size="small" color="primary" label={m.mood} sx={{ textTransform: 'capitalize' }} onClick={moodClicked(m)} />
							))}
						</Stack>
					</Stack>
				)
			},
			{
				field: 'beatsPerMinute',
				headerName: 'BPM',
				width: 86,
				align: 'right',
				headerAlign: 'right',
				disableColumnMenu: true,
				renderCell: (params) => (
					<Typography variant="body2" color="textPrimary" sx={{ fontFeatureSettings: `'tnum'` }}>
						{(params.row as Song).beatsPerMinute ? `${(params.row as Song).beatsPerMinute} BPM` : ''}
					</Typography>
				)
			},
			{
				field: 'duration',
				headerName: 'Duration',
				width: 114,
				align: 'right',
				headerAlign: 'right',
				disableColumnMenu: true,
				valueGetter: (value, row) => +(row as Song).duration,
				renderCell: (params) => (
					<Typography variant="body2" color="textPrimary" sx={{ fontFeatureSettings: `'tnum'` }}>{formatDuration((params.row as Song).duration)}</Typography>
				)
			},
			{
				field: 'mp3FileS3Key',
				headerName: '',
				width: 172,
				disableColumnMenu: true,
				sortable: false,
				renderCell: (params) => (
					<Stack direction="row" spacing={1} height="100%" alignItems="center">
						<IconButton size="large" sx={{ width: 56, height: 56 }} disabled={!(params.row as Song).mp3FileS3Key} onClick={handlePlayPause(params.row as Song)}>
							<FontAwesomeIcon icon={{ prefix: 'fas', iconName: getPlayStateIcon(params.row as Song) }} style={{ color: theme.palette.primary.contrastText, fontSize: 24 }} />
						</IconButton>
						<IconButton size="large" sx={{ width: 56, height: 56 }} disabled={!(params.row as Song).mp3FileS3Key} onClick={handleDownloadClicked(params.row as Song)}>
							<FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'download' }} style={{ color: theme.palette.primary.contrastText, fontSize: 24 }} />
						</IconButton>
					</Stack>
				)
			},
			{
				field: 'album',
				headerName: 'Album',
				hideable: true,
				valueGetter: (value, row) => (row as Song).album?.name || ''
			},
			{
				field: 'genres',
				headerName: 'Genres',
				hideable: true,
				valueGetter: (value, row) => (row as Song).genres.map(g => g.genre).join(', ')
			},
			{
				field: 'moods',
				headerName: 'Moods',
				hideable: true,
				valueGetter: (value, row) => (row as Song).moods.map(m => m.mood).join(', ')
			}
		]);
	}, [genreClicked, getPlayStateIcon, handlePlayPause, moodClicked, openSongDialog, theme.palette.primary.contrastText]);

	const ListViewCell = useCallback((params: GridRenderCellParams) => {
		return (
			<Stack direction="row" alignItems="center" gap={1} width="100%" height="100%">
				<Tooltip title={(params.row as Song).album?.name || ''} placement="left">
					<Box
						component="img"
						src={getPublicSongImageUrl(params.row as Song)}
						alt={(params.row as Song).title}
						width={50}
						height={50}
						borderRadius={0.5}
					/>
				</Tooltip>
				<Stack flex="1 1 auto" minWidth={0}>
					<Typography
						variant="body1"
						color="textPrimary"
						sx={{
							overflowX: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap'
						}}
					>
						{params.row.title}
					</Typography>
					<Stack direction="row" spacing={2} overflow="hidden">
						{params.row.genres.map((g: Genre) => (
							<Link key={g.id} variant="body2" color="secondary" sx={{ textTransform: 'capitalize' }} onClick={genreClicked(g)}>{g.genre}</Link>
						))}
						{(params.row as Song).moods.map(m => (
							<Link key={m.id} variant="body2" color="primary" sx={{ textTransform: 'capitalize' }} onClick={moodClicked(m)}>{m.mood}</Link>
						))}
					</Stack>
				</Stack>
				<Box>
					<IconButton size="large" sx={{ width: 50, height: 50 }} disabled={!(params.row as Song).mp3FileS3Key} onClick={handlePlayPause(params.row as Song)}>
						<FontAwesomeIcon icon={{ prefix: 'fas', iconName: getPlayStateIcon(params.row as Song) }} style={{ color: theme.palette.primary.contrastText }} />
					</IconButton>
				</Box>
			</Stack>
		);
	}, [genreClicked, getPlayStateIcon, handlePlayPause, moodClicked, theme.palette.primary.contrastText]);

	const listColDef: GridListColDef = {
		field: 'listColumn',
		renderCell: ListViewCell
	}

	const rowClicked = async (params: GridRowParams) => {
		const song = params.row as Song;

		posthog.capture('song_opened', {
			songId: song.id,
			songTitle: song.title,
			songAlbum: song.album?.name,
			origin: 'row_click'
		});

		openSongDialog(song);
	};

	return (
		<ScreenBase
			scrollableChildren={(
				<Box sx={{ height: 'calc(100dvh - (73.5px + 69.91px))' }}>
					<DataGridPremium
						apiRef={gridApiRef}
						loading={loading}
						columns={columns}
						rows={rows}
						rowHeight={66}
						// pagination
						// pageSizeOptions={[10, 25, 50]}
						sx={dataGridStyles}
						onRowClick={rowClicked}
						disableRowSelectionOnClick={true}
						hideFooter={true}
						slots={{ toolbar: CustomToolbar }}
						rowBufferPx={400}
						initialState={{
							columns: {
								columnVisibilityModel: {
									genres: false,
									moods: false,
									album: false
								}
							},
							filter: {
								filterModel: {
									items: [],
									quickFilterExcludeHiddenColumns: false
								}
							},
							// pagination: {
							// 	paginationModel: {
							// 		pageSize: 25,
							// 		page: 1
							// 	}
							// }
						}}
						unstable_listView={stops.isXs || stops.isSm || stops.isMd}
						unstable_listColumn={listColDef}
					/>
				</Box>
			)}
			miniFooter={true}
		/>
	);
};

export default TunesScreen;
